import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Modal,
  Table,
  Button,
  Space,
  Tag,
  Input,
  Checkbox,
  message,
  Divider,
  Typography
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import warehouseApi from "modules/client/warehouses/api/warehouseApi";
import "./WarehouseInventoryModal.scss";

const { Text } = Typography;

const WarehouseInventoryModal = ({ visible, onCancel, onConfirm, pickupLocationId }) => {
  const [warehouses, setWarehouses] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedItems, setSelectedItems] = useState({});
  const [expandedWarehouse, setExpandedWarehouse] = useState(null);

  const loadWarehousesAndInventory = useCallback(async () => {
    setLoading(true);
    try {
      const warehousesData = await warehouseApi.getWarehousesByLocation(pickupLocationId);
      setWarehouses(warehousesData);

      const allInventory = await warehouseApi.getInventoryItems();
      setInventoryItems(allInventory);
    } catch (error) {
      console.error("Error loading warehouse data:", error);
      message.error("Failed to load warehouse inventory for this location");
    } finally {
      setLoading(false);
    }
  }, [pickupLocationId]);

  useEffect(() => {
    if (visible && pickupLocationId) {
      loadWarehousesAndInventory();
    }
  }, [visible, pickupLocationId, loadWarehousesAndInventory]);

  const groupedInventory = useMemo(() => {
    const grouped = {};
    inventoryItems.forEach((item) => {
      if (!grouped[item.warehouseId]) {
        grouped[item.warehouseId] = [];
      }
      grouped[item.warehouseId].push(item);
    });
    return grouped;
  }, [inventoryItems]);

  const filteredInventory = useMemo(() => {
    if (!searchText) return groupedInventory;

    const search = searchText.toLowerCase();
    const filtered = {};

    Object.keys(groupedInventory).forEach((warehouseId) => {
      const items = groupedInventory[warehouseId].filter(
        (item) =>
          item.name?.toLowerCase().includes(search) ||
          item.itemId?.toLowerCase().includes(search) ||
          item.serialNumbersList?.some(
            (serial) =>
              serial.serialNumber?.toLowerCase().includes(search) ||
              serial.itemAttribute?.toLowerCase().includes(search) ||
              serial.po?.toLowerCase().includes(search)
          )
      );
      if (items.length > 0) {
        filtered[warehouseId] = items;
      }
    });

    return filtered;
  }, [groupedInventory, searchText]);

  const handleSerialSelection = (itemId, serial, checked) => {
    setSelectedItems((prev) => {
      const newSelected = { ...prev };
      if (!newSelected[itemId]) {
        newSelected[itemId] = { serials: [] };
      }
      if (checked) {
        newSelected[itemId].serials.push(serial);
      } else {
        newSelected[itemId].serials = newSelected[itemId].serials.filter(
          (s) => s.serialNumber !== serial.serialNumber
        );
        if (newSelected[itemId].serials.length === 0) {
          delete newSelected[itemId];
        }
      }
      return newSelected;
    });
  };

  const handleItemSelection = (item, checked) => {
    setSelectedItems((prev) => {
      const newSelected = { ...prev };
      if (checked) {
        newSelected[item.id] = {
          item,
          serials: item.serialNumbersList || []
        };
      } else {
        delete newSelected[item.id];
      }
      return newSelected;
    });
  };

  const isItemSelected = (itemId) => {
    return !!selectedItems[itemId];
  };

  const isSerialSelected = (itemId, serial) => {
    return selectedItems[itemId]?.serials?.some(
      (s) => s.serialNumber === serial.serialNumber
    );
  };

  const handleConfirm = () => {
    const summary = Object.values(selectedItems).map((selection) => ({
      item: selection.item,
      serials: selection.serials
    }));

    if (summary.length === 0) {
      message.warning("Please select at least one item");
      return;
    }

    onConfirm(summary);
    handleCancel();
  };

  const handleCancel = () => {
    setSelectedItems({});
    setSearchText("");
    setExpandedWarehouse(null);
    onCancel();
  };

  const selectedCount = Object.keys(selectedItems).length;
  const totalSerials = Object.values(selectedItems).reduce(
    (sum, item) => sum + item.serials.length,
    0
  );

  const warehouseColumns = [
    {
      title: "Warehouse",
      key: "warehouse",
      render: (_, warehouse) => (
        <Space>
          <Text strong>{warehouse.name}</Text>
          <Tag color="blue">{groupedInventory[warehouse.id]?.length || 0} items</Tag>
        </Space>
      )
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      render: (_, warehouse) => (
        <Button
          type="link"
          onClick={() =>
            setExpandedWarehouse(expandedWarehouse === warehouse.id ? null : warehouse.id)
          }
        >
          {expandedWarehouse === warehouse.id ? "Collapse" : "Expand"}
        </Button>
      )
    }
  ];

  const inventoryColumns = [
    {
      title: "Select",
      key: "select",
      width: 60,
      render: (_, item) => (
        <Checkbox
          checked={isItemSelected(item.id)}
          onChange={(e) => handleItemSelection(item, e.target.checked)}
        />
      )
    },
    {
      title: "Item ID",
      dataIndex: "itemId",
      key: "itemId",
      width: 120
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name"
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      width: 100
    },
    {
      title: "Serial Numbers",
      key: "serials",
      render: (_, item) => {
        if (!item.serialNumbersList || item.serialNumbersList.length === 0) {
          return <Text type="secondary">No serials</Text>;
        }
        return (
          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            {item.serialNumbersList.map((serial, idx) => (
              <div key={idx} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Checkbox
                  checked={isSerialSelected(item.id, serial)}
                  onChange={(e) =>
                    handleSerialSelection(item.id, serial, e.target.checked)
                  }
                  disabled={!isItemSelected(item.id)}
                />
                <Tag color="blue">{serial.serialNumber}</Tag>
                {serial.itemAttribute && <Tag color="green">{serial.itemAttribute}</Tag>}
                {serial.po && <Text type="secondary">PO: {serial.po}</Text>}
              </div>
            ))}
          </Space>
        );
      }
    }
  ];

  if (!pickupLocationId) {
    return (
      <Modal
        visible={visible}
        title="Select from Warehouse"
        width="90%"
        onCancel={handleCancel}
        footer={
          <Space>
            <Button onClick={handleCancel}>Cancel</Button>
          </Space>
        }
      >
        <div style={{ textAlign: "center", padding: "40px" }}>
          <Text type="danger">
            No pickup location selected for this route. Please set a pickup location
            first.
          </Text>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      visible={visible}
      title="Select from Warehouse"
      width="90%"
      onCancel={handleCancel}
      footer={
        <Space>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button type="primary" onClick={handleConfirm}>
            Confirm Selection ({selectedCount} items, {totalSerials} serials)
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Input
          placeholder="Search by item name, ID, serial number, attribute, or PO..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
          style={{ width: "100%" }}
        />

        <div>
          {warehouses
            .filter((warehouse) => filteredInventory[warehouse.id])
            .map((warehouse) => (
              <div key={warehouse.id} style={{ marginBottom: 24 }}>
                <Table
                  dataSource={[warehouse]}
                  columns={warehouseColumns}
                  pagination={false}
                  showHeader={false}
                  rowKey="id"
                />
                {expandedWarehouse === warehouse.id && (
                  <div style={{ marginTop: 8, marginLeft: 24 }}>
                    <Table
                      dataSource={filteredInventory[warehouse.id]}
                      columns={inventoryColumns}
                      pagination={false}
                      rowKey="id"
                      loading={loading}
                      size="small"
                    />
                  </div>
                )}
              </div>
            ))}
        </div>

        {selectedCount > 0 && (
          <>
            <Divider />
            <div>
              <Text strong>Selection Summary:</Text>
              <div style={{ marginTop: 12 }}>
                {Object.values(selectedItems).map((selection) => {
                  const warehouse = warehouses.find(
                    (w) => w.id === selection.item?.warehouseId
                  );
                  return (
                    <div
                      key={selection.item?.id}
                      style={{ marginBottom: 12, padding: 8, background: "#f5f5f5" }}
                    >
                      <Text strong>
                        {warehouse?.name} - {selection.item?.name}
                      </Text>
                      <div style={{ marginTop: 4 }}>
                        <Text type="secondary">
                          {selection.serials.length} serial(s) selected
                        </Text>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </Space>
    </Modal>
  );
};

export default WarehouseInventoryModal;
